import React, { useState, useEffect, useCallback } from "react";
import {
  addContentTopic,
  updateContentTopic,
  getContentTopicById,
} from "../../../services/contentTopicsService";
import { validateContentTopic } from "../../../utils/validation";
import { generateSlug } from "../../../utils/cmsUtils";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { handleErrors } from "../../../utils/errorHandler";
import ComponentHeader from "../../Common/OtherElements/ComponentHeader";

const INITIAL_FORM_STATE = {
  topicName: "",
  topicSlug: "",
  displayOrder: "",
};

const INITIAL_ERRORS_STATE = {
  topicName: "",
};

const mapTopicResponse = (data) => ({
  topicName: data.topicName || data.TopicName || "",
  topicSlug: data.topicSlug || data.TopicSlug || "",
  displayOrder:
    data.displayOrder !== undefined && data.displayOrder !== null
      ? String(data.displayOrder)
      : data.DisplayOrder !== undefined && data.DisplayOrder !== null
      ? String(data.DisplayOrder)
      : "1",
});

const resolveDisplayOrderForAdd = (topicsCount) => {
  const count = Number(topicsCount) || 0;
  return count > 0 ? count + 1 : 1;
};

export const AddContentTopic = ({
  editMode = false,
  initialData = {},
  onSuccess,
  setSelectedTopic,
  setEditMode,
  topicsCount = 0,
}) => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [errors, setErrors] = useState(INITIAL_ERRORS_STATE);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const topicId = initialData?.id ?? initialData?.Id;
      if (editMode && topicId) {
        try {
          const data = await getContentTopicById(topicId);
          setFormData(mapTopicResponse(data));
        } catch (error) {
          handleErrors(error);
        }
      } else {
        setFormData(INITIAL_FORM_STATE);
      }
    };
    fetchData();
  }, [editMode, initialData]);

  const handleInputChange = ({ target: { name, value } }) => {
    if (name === "topicName") {
      setFormData((prev) => ({
        ...prev,
        topicName: value,
        topicSlug: generateSlug(value),
      }));
    }
    setErrors((prev) => ({ ...prev, topicName: "" }));
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_STATE);
    setErrors(INITIAL_ERRORS_STATE);
  };

  const buildSubmitPayload = useCallback(() => {
    const trimmedName = formData.topicName.trim();
    const topicSlug = generateSlug(trimmedName);
    const displayOrder = editMode
      ? Number(formData.displayOrder) || 1
      : resolveDisplayOrderForAdd(topicsCount);

    return {
      topicName: trimmedName,
      topicSlug,
      displayOrder,
    };
  }, [formData, editMode, topicsCount]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      const prepared = buildSubmitPayload();
      const dataForValidation = {
        ...formData,
        topicName: prepared.topicName,
        topicSlug: prepared.topicSlug,
        displayOrder: String(prepared.displayOrder),
      };

      const { valid, errors: validationErrors } =
        validateContentTopic(dataForValidation);
      setErrors(validationErrors);

      if (!valid) return;

      const topicId = initialData?.id ?? initialData?.Id;
      const payload = { ...prepared };

      if (editMode) {
        payload.id = topicId;
      }

      try {
        setIsButtonDisabled(true);
        const action = editMode ? updateContentTopic : addContentTopic;
        const response = await action(payload);

        if (response?.data?.isSuccess === false) {
          toast.error(
            response.data.message || "Request failed. Please try again."
          );
          return;
        }

        toast.success(`Tag ${editMode ? "updated" : "added"} successfully!`);
        resetForm();
        onSuccess?.();
        setEditMode(false);
      } catch (error) {
        handleErrors(error);
      } finally {
        setIsButtonDisabled(false);
      }
    },
    [
      formData,
      editMode,
      initialData,
      onSuccess,
      setEditMode,
      buildSubmitPayload,
    ]
  );

  const handleAddNewClick = () => {
    resetForm();
    setSelectedTopic(null);
    setEditMode(false);
  };

  return (
    <>
      <ComponentHeader title="Tag Master" />

      <div className="row">
        <div className="col-xxl-12">
          <div className="card mt-xxl-n5">
            <div className="card-header">
              <h5 className="mb-sm-1 mt-sm-1">
                {editMode ? "Update Tag" : "Add Tag"}
              </h5>
            </div>

            <div className="card-body p-4">
              <form onSubmit={handleSubmit} method="POST">
                <div className="row g-3 align-items-end xc-inline-form">
                  <div className="col-12 col-md-6 col-lg-4">
                    <label htmlFor="topicName" className="form-label">
                      Tag Name <span className="required-field">*</span>
                    </label>
                    <input
                      type="text"
                      name="topicName"
                      id="topicName"
                      placeholder="e.g. Middle East War"
                      value={formData.topicName}
                      onChange={handleInputChange}
                      className={`form-control ${
                        errors.topicName ? "is-invalid" : ""
                      }`}
                    />
                    {errors.topicName && (
                      <div className="invalid-feedback d-block">
                        {errors.topicName}
                      </div>
                    )}
                  </div>

                  <div className="col-12 col-md-auto d-flex flex-wrap gap-2 xc-inline-form__actions">
                    <button
                      type="submit"
                      className="btn btn-secondary"
                      disabled={isButtonDisabled}
                    >
                      {isButtonDisabled
                        ? editMode
                          ? "Updating"
                          : "Saving"
                        : editMode
                        ? "Update"
                        : "Save"}
                    </button>
                    {editMode && (
                      <button
                        type="button"
                        onClick={handleAddNewClick}
                        className="btn btn-danger"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
